import React from "react";
import { motion } from "framer-motion";
import {
  Truck,
  Utensils,
  Wifi,
  ParkingCircle,
  Zap,
  Tent,
  Package,
  ShieldCheck,
  PlaneLanding,
  Warehouse,
  ShowerHead,
} from "lucide-react";

const features = [
  { icon: Truck, title: "Unit Base", desc: "Hardstand for unit vehicles, trucks and trailers across the 45 ha backlot." },
  { icon: Utensils, title: "Catering Hub", desc: "Capacity for full-crew catering and dining with plug-in facilities for catering trucks and equipment." },
  { icon: Wifi, title: "Connectivity", desc: "Designed for fibre uplink and Starlink redundancy across stages and offices." },
  { icon: ParkingCircle, title: "Crew Parking", desc: "Dedicated areas for cast, crew, talent and contractor traffic across the site." },
  { icon: Zap, title: "Power & Fuel", desc: "Industrial 3-phase distribution, generator points." },
  { icon: Tent, title: "Production Offices", desc: "Working spaces for AD, art, costume, accounts and supporting industry providers." },
  { icon: PlaneLanding, title: "Helicopter & Fixed-Wing Landing", desc: "On-site landing options for helicopters and fixed-wing aircraft across multiple stations. Suited to talent, aerial units and rapid crew transfers." },
  { icon: Warehouse, title: "Modern Helicopter Hangar", desc: "Purpose-built modern helicopter hangar available just 10 km from the studio hub for secure storage and quick rotations." },
  { icon: ShowerHead, title: "Amenities Block", desc: "Crew amenities block with 2 × toilets, showers and changing facilities — keeping cast and crew comfortable on long shoot days." },
  { icon: Package, title: "Lock-up Storage", desc: "Secure space for camera, lighting, props and stunt rigs across multiple departments." },
  { icon: ShieldCheck, title: "Multi-use Capable", desc: "Built to also support events, concerts, civil defence, training exercises and weddings." },
];

export default function BaseCamp() {
  return (
    <section
      id="basecamp"
      data-testid="basecamp-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-[#E2B464]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E2B464]">
                04 — Base Camp Hub
              </span>
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              The whole production,{" "}
              <span className="italic text-[#E2B464]">on one site.</span>
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-zinc-400 leading-relaxed text-base self-end">
            Victoria Flats was designed from the ground up as a film base, a 45
            hectare site (111.20 acres) in Gibbston Character Zoning. It has the
            scope to also host events, concerts, civil defence operations, staff
            training and team building.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`basecamp-feature-${i}`}
              className="bg-[#0a0a0a] p-7 md:p-9 group hover:bg-[#111] transition-colors"
            >
              <f.icon className="w-6 h-6 text-[#E2B464] mb-8 group-hover:scale-110 transition-transform" strokeWidth={1.4} />
              <h3 className="font-display text-xl text-white tracking-tight mb-3">
                {f.title}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
